import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Typography,
  Box,
  Paper,
  Divider,
  Chip,
  Stack,
  IconButton,
  Tooltip
} from '@mui/material';
import {
  Add as AddIcon,
  Close as CloseIcon,
  AccessTime as TimeIcon
} from '@mui/icons-material';
import { useAuth } from '../context/AuthContext';
import { API_URL } from '../config';
import { getSourceContext, getFriendlySourceLabel, parseNotesBlob } from '../utils/noteUtils';
import NoteCard from './notes/NoteCard';

const NotesDialog = ({ 
  open, 
  onClose, 
  title, 
  entityType, // 'enquiry', 'offer', 'order'
  entityId,
  initialNotes = [],
  defaultNoteType = 'general',
  onNotesUpdate
}) => {
  const { user, getToken } = useAuth();
  const [notes, setNotes] = useState(initialNotes);
  const [newNote, setNewNote] = useState('');
  const [noteType, setNoteType] = useState(defaultNoteType);
  const [loading, setLoading] = useState(false);

  // Reset notes when dialog opens with new data
  useEffect(() => {
    if (open) {
      // Combine initial notes with localStorage notes
      const storageKey = `notes_${entityType}_${entityId}`;
      const storageNotes = JSON.parse(localStorage.getItem(storageKey) || '[]');
      
      // Flatten each incoming note's content into individually-attributed
      // notes. Callers typically pass one entry per DB field whose `content`
      // is actually a blob of multiple notes joined together - parsing here
      // means every note is displayed on its own, consistently.
      const flattenedInitialNotes = initialNotes.flatMap(note => {
        if (!note.content) return [note];
        const parsed = parseNotesBlob(note.content, {
          idPrefix: `${note.id != null ? note.id : 'note'}`,
          createdAt: note.createdAt,
          createdBy: note.createdBy,
          noteType: note.noteType
        });
        return parsed.length > 0 ? parsed : [note];
      });

      // Merge notes from database and localStorage, removing duplicates
      const allNotes = [...storageNotes, ...flattenedInitialNotes];
      const uniqueNotes = allNotes.reduce((acc, note) => {
        // Use content and timestamp to identify duplicates
        const key = `${note.content}_${note.createdAt}`;
        if (!acc.some(n => `${n.content}_${n.createdAt}` === key)) {
          acc.push(note);
        }
        return acc;
      }, []);
      
      // Sort by creation date (newest first)
      uniqueNotes.sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt));
      
      setNotes(uniqueNotes);
      setNewNote('');
      setNoteType(defaultNoteType); // Reset note type to default
    }
  }, [open, initialNotes, entityType, entityId]);

  const handleAddNote = async () => {
    if (!newNote.trim()) return;

    setLoading(true);
    try {
      // Build the note for immediate display: clean content plus a short
      // source label, rather than baking "(date) User from Source:" directly
      // into the text. The backend independently builds its own structured
      // string (see notes.js) when persisting.
      const userName = user?.name || 'Current User';
      const sourceContext = getSourceContext(entityType, noteType);

      const noteData = {
        id: Date.now(), // Temporary ID for display
        content: newNote.trim(),
        createdBy: userName,
        createdAt: new Date().toISOString(),
        sourceLabel: getFriendlySourceLabel(sourceContext),
        isNew: true,
        noteType: noteType // Include the note type for proper filtering
      };

      // Try to save to backend API first
      let backendSaved = false;
      try {
        // Get auth token with proper Auth0 configuration
        const token = await getToken({
          authorizationParams: {
            scope: 'openid profile email read:roles'
          }
        });

        if (token) {
          // Call backend API to save the note
          const response = await fetch(`${API_URL}/notes/${entityType}/${entityId}`, {
            method: 'POST',
            headers: {
              'Authorization': `Bearer ${token}`,
              'Content-Type': 'application/json'
            },
            body: JSON.stringify({
              content: newNote.trim(),
              noteType: noteType
            })
          });

          if (response.ok) {
            const result = await response.json();
            backendSaved = true;
          } else {
            console.warn('Backend save failed, falling back to localStorage');
          }
        }
      } catch (backendError) {
        console.warn('Backend API unavailable, using localStorage fallback:', backendError.message);
      }

      // Fallback: Save to localStorage if backend fails
      if (!backendSaved) {
        const storageKey = `notes_${entityType}_${entityId}`;
        const existingNotesStorage = JSON.parse(localStorage.getItem(storageKey) || '[]');
        const updatedStorageNotes = [noteData, ...existingNotesStorage];
        localStorage.setItem(storageKey, JSON.stringify(updatedStorageNotes));
      }

      // Add to local state
      const updatedNotes = [noteData, ...notes];
      setNotes(updatedNotes);
      setNewNote('');
      setNoteType(defaultNoteType); // Reset note type to default

      // Notify parent component of the update only if backend save was successful
      // If we only saved to localStorage, don't trigger parent reload as it will overwrite our localStorage data
      if (onNotesUpdate && backendSaved) {
        onNotesUpdate(updatedNotes, noteData);
      } else if (onNotesUpdate && !backendSaved) {
        // For localStorage-only saves, just trigger a local UI update without data reload
        onNotesUpdate(updatedNotes, noteData, { localOnly: true });
      }
    } catch (error) {
      console.error('Error adding note:', error);
      // Show error to user
      alert(`Failed to save note: ${error.message}`);
    } finally {
      setLoading(false);
    }
  };



  return (
    <Dialog 
      open={open} 
      onClose={onClose} 
      maxWidth="md" 
      fullWidth
      PaperProps={{
        sx: { height: '80vh', display: 'flex', flexDirection: 'column' }
      }}
    >
      <DialogTitle sx={{ pb: 1, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Box>
          <Typography variant="h6">{title} - Notes</Typography>
          <Typography variant="caption" color="text.secondary">
            {entityType} #{entityId}
          </Typography>
        </Box>
        <IconButton onClick={onClose} size="small">
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent sx={{ display: 'flex', flexDirection: 'column', flex: 1, gap: 2 }}>
        {/* Add New Note Section */}
        <Paper sx={{ p: 2, bgcolor: 'grey.50' }}>
          <Typography variant="subtitle2" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <AddIcon fontSize="small" />
            Add New Note
          </Typography>
          
          {/* Note Type Selector */}
          <Box sx={{ mb: 2 }}>
            <Typography variant="caption" gutterBottom display="block">
              Note Type:
            </Typography>
            <Stack direction="row" spacing={1}>
              <Chip
                label="General"
                variant={noteType === 'general' ? 'filled' : 'outlined'}
                color={noteType === 'general' ? 'primary' : 'default'}
                onClick={() => setNoteType('general')}
                size="small"
              />
              <Chip
                label="Design"
                variant={noteType === 'design' ? 'filled' : 'outlined'}
                color={noteType === 'design' ? 'secondary' : 'default'}
                onClick={() => setNoteType('design')}
                size="small"
              />
            </Stack>
          </Box>
          
          <TextField
            fullWidth
            multiline
            rows={3}
            placeholder="Enter your note here..."
            value={newNote}
            onChange={(e) => setNewNote(e.target.value)}

            disabled={loading}
            sx={{ mb: 1 }}
          />
          <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
            <Button
              variant="contained"
              size="small"
              startIcon={<AddIcon />}
              onClick={handleAddNote}
              disabled={!newNote.trim() || loading}
            >
              Add Note
            </Button>
          </Box>
        </Paper>

        {/* Notes History - Categorized into three sections */}
        <Box sx={{ flex: 1, overflow: 'auto' }}>
          <Typography variant="subtitle2" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <TimeIcon fontSize="small" />
            Notes History ({notes.length})
          </Typography>
          
          {notes.length === 0 ? (
            <Paper sx={{ p: 3, textAlign: 'center', bgcolor: 'grey.50' }}>
              <Typography variant="body2" color="text.secondary">
                No notes available. Add the first note above.
              </Typography>
            </Paper>
          ) : (
            <Stack spacing={3}>
              {/* Sales Notes Section */}
              {(() => {
                const salesNotes = notes.filter(note => !note.noteType || note.noteType === 'general' || note.noteType === 'sales');
                return salesNotes.length > 0 && (
                  <Box>
                    <Typography variant="subtitle2" color="primary" fontWeight="bold" gutterBottom>
                      📊 Sales Notes ({salesNotes.length})
                    </Typography>
                    <Stack spacing={1}>
                      {salesNotes.map((note, index) => (
                        <NoteCard key={note.id || `sales-${index}`} note={note} color="primary" bgColor="blue.50" borderColor="blue.200" />
                      ))}
                    </Stack>
                  </Box>
                );
              })()}

              {/* Design Notes Section */}
              {(() => {
                const designNotes = notes.filter(note => note.noteType === 'design');
                return designNotes.length > 0 && (
                  <Box>
                    <Typography variant="subtitle2" color="secondary" fontWeight="bold" gutterBottom>
                      🎨 Design Notes ({designNotes.length})
                    </Typography>
                    <Stack spacing={1}>
                      {designNotes.map((note, index) => (
                        <NoteCard key={note.id || `design-${index}`} note={note} color="secondary" bgColor="purple.50" borderColor="purple.200" />
                      ))}
                    </Stack>
                  </Box>
                );
              })()}

              {/* Customer Notes Section */}
              {(() => {
                const customerNotes = notes.filter(note => note.noteType === 'customer');
                return customerNotes.length > 0 && (
                  <Box>
                    <Typography variant="subtitle2" color="info.main" fontWeight="bold" gutterBottom>
                      👤 Customer Notes ({customerNotes.length})
                    </Typography>
                    <Stack spacing={1}>
                      {customerNotes.map((note, index) => (
                        <NoteCard key={note.id || `customer-${index}`} note={note} color="info" bgColor="info.50" borderColor="info.200" />
                      ))}
                    </Stack>
                  </Box>
                );
              })()}
            </Stack>
          )}
        </Box>
      </DialogContent>

      <DialogActions sx={{ px: 3, py: 2 }}>
        <Button onClick={onClose} variant="outlined">
          Close
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default NotesDialog;
